var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var AggregateV2Config_1;
/* eslint-disable import/no-duplicates */
import '@symon-ai/wisepipe-base-pipe'; // force import for JsonSchema
import { AggregateColumn, ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult, ToolValidationState } from './validation/toolValidationResult';
import { buildInputsMap, validateSourceWithDetail } from './validation/validateHelper';
const schema = {
    $id: 'urn:wisepipe-common-pipe:AggregateV2Config',
    type: 'object',
    properties: {
        aggregateColumns: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-base-pipe:AggregateColumn' },
            uniqueItemProperties: ['alias']
        },
        groupByColumns: {
            type: 'array',
            items: {
                oneOf: [
                    {
                        $ref: 'urn:wisepipe-base-pipe:ColumnToolVariable'
                    },
                    { type: 'string' }
                ]
            },
            uniqueItems: true
        },
        decimals: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:NumberToolVariable'
                },
                { type: 'integer', minimum: 0, maximum: 14 }
            ]
        },
        includeAllNulls: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:BoolToolVariable'
                },
                { type: 'boolean' }
            ]
        }
    },
    required: ['aggregateColumns', 'groupByColumns']
};
let AggregateV2Config = AggregateV2Config_1 = class AggregateV2Config extends ToolConfig {
    constructor(groupByColumns, aggregateColumns, decimals, customFormats, includeAllNulls) {
        super(ToolType.AGGREGATEV2, customFormats);
        this.groupByColumns = groupByColumns;
        this.aggregateColumns = aggregateColumns;
        this.includeAllNulls = includeAllNulls;
        if (decimals !== undefined)
            this.decimals = decimals;
        JSON.validate(this, AggregateV2Config_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return AggregateV2Config_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return AggregateV2Config_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return AggregateV2Config_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return AggregateV2Config_1.outs;
    }
    static get in() {
        return this.ins[0];
    }
    static get out() {
        return this.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        const ops = Object.values(AggregateColumn.Operation);
        return new ToolConfigInfo(ToolType.AGGREGATE.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE], this.ins, this.outs, ['group by', 'average', 'mean', 'total', 'add', 'maximum', 'minimum', 'total', 'subtotal', 'calculate'].concat(ops), true);
    }
    applyParameters(parameters) {
        const baseVals = this.getBaseValues(parameters);
        return new AggregateV2Config_1(baseVals.groupByColumns, parameters.getAppliedVals(this.aggregateColumns), baseVals.decimals, this.customFormats, baseVals.includeAllNulls);
    }
    getBaseValues(parameters) {
        return {
            groupByColumns: parameters.getStrArrayVals(this.groupByColumns),
            aggregateColumns: this.aggregateColumns.map((a) => a.getBaseValues(parameters)),
            decimals: parameters.getNumOrUndefVal(this.decimals),
            includeAllNulls: parameters.getBoolOrUndefVal(this.includeAllNulls)
        };
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        if (source) {
            const exp = tsb.export(this.out);
            const baseVals = this.getBaseValues(iConfigHelper.getParameters());
            const dependents = this.getGroupByDependents(exp, this.in, baseVals.groupByColumns);
            // also add the actual column we are aggregating as a dependee
            baseVals.aggregateColumns.forEach((a) => {
                if (a.column)
                    dependents.push(this.getDependent(exp, this.in, a.column));
            });
            const newColumnFormats = new Map();
            baseVals.aggregateColumns.forEach((a) => {
                if (a.operation === AggregateColumn.Operation.COUNT) {
                    exp.makeNew(new Column(a.alias, 'number'), dependents, true);
                }
                else {
                    const srcColumn = source.find((s) => s.name === a.column);
                    exp.makeNew(new Column(a.alias, srcColumn.type), dependents, true);
                    if (srcColumn.customFormat) {
                        newColumnFormats.set(a.alias, srcColumn.customFormat.copyWithAutoGeneratedTrue());
                    }
                }
            });
            exp
                .initializeColumnFormats()
                .setColumnFormats(newColumnFormats)
                .setColumnFormats(this.customFormats)
                .setUniqueKeys(baseVals.groupByColumns);
        }
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        const baseVals = this.getBaseValues(iConfigHelper.getParameters());
        const groupByColumns = new Set(baseVals.groupByColumns);
        const inputs = buildInputsMap(baseVals.groupByColumns, []);
        const numOps = new Set([
            AggregateColumn.Operation.SUM,
            AggregateColumn.Operation.AVERAGE,
            AggregateColumn.Operation.MEDIAN,
            AggregateColumn.Operation.STDEV
        ]);
        const nonBoolOps = new Set([AggregateColumn.Operation.MIN, AggregateColumn.Operation.MAX]);
        const boolOps = new Set([AggregateColumn.Operation.OR, AggregateColumn.Operation.AND]);
        for (const column of baseVals.aggregateColumns) {
            if (groupByColumns.has(column.alias)) {
                return new ToolValidationResult(ToolValidationState.BAD_METADATA, `Alias cannot be the same as a group by column, Invalid alias: ${column.alias}`, `Please change the alias to be distinct from the group by columns: ${Array.from(groupByColumns).join(', ')}`);
            }
            if (column.column !== undefined) {
                if (groupByColumns.has(column.column)) {
                    return new ToolValidationResult(ToolValidationState.BAD_METADATA, `The columns specified in the groupBy array must be distinct from the columns specified in the aggregateColumns. Invalid columns: ${column.column}`, `Try adjusting the group by columns or the aggregate columns to meet the requirements.`);
                }
                const types = [];
                if (numOps.has(column.operation)) {
                    types.push('number');
                    types.push('decimal');
                }
                else if (nonBoolOps.has(column.operation)) {
                    types.push('string');
                    types.push('number');
                    types.push('date');
                    types.push('decimal');
                }
                else if (boolOps.has(column.operation)) {
                    types.push('boolean');
                }
                inputs.set(column.column, types);
            }
        }
        const sourceValidationResult = validateSourceWithDetail(source, inputs, []);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
AggregateV2Config = AggregateV2Config_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.AGGREGATEV2)
], AggregateV2Config);
export { AggregateV2Config };
//# sourceMappingURL=aggregateV2Config.js.map